/**
 * SoundManager
 * 레트로 게임 8bit 사운드 효과 관리
 * - ZzFX 기반 (실시간 8bit 사운드 생성)
 * - 효과음 재생 및 제어
 * - 볼륨 및 음소거 관리
 */

import { zzfx } from 'zzfx';

/**
 * 사운드 타입 정의
 */
type SoundType = 'correct' | 'wrong' | 'click' | 'combo' | 'levelup' | 'bgm';

/**
 * SoundManager 클래스
 * 싱글톤 패턴으로 구현
 */
class SoundManager {
  private isMuted: boolean = false;
  private volume: number = 0.7;

  /**
   * ZzFX 사운드 파라미터 정의
   * [volume, randomness, frequency, attack, sustain, release, shape, shapeCurve, vibrato, vibratoSpeed, ...]
   */
  private soundParams: Record<SoundType, any> = {
    // 정답: 밝고 경쾌한 팡파레 (상승하는 멜로디)
    correct: [1, 0, 523, 0.01, 0.1, 0.2, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.5, 0, 0],

    // 오답: 부드러운 하강음 (실망)
    wrong: [0.8, 0, 200, 0.01, 0.2, 0.3, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.3, 0, 0],

    // 클릭: 짧고 가벼운 틱 소리
    click: [0.5, 0, 1000, 0, 0.01, 0.01, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.1, 0, 0],

    // 콤보: 코인 획득 소리 (반짝)
    combo: [1, 0, 800, 0, 0.05, 0.15, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.5, 0, 0],

    // 레벨업: 상승하는 축하 사운드
    levelup: [1.2, 0, 400, 0.01, 0.2, 0.4, 1, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0],

    // BGM은 ZzFX로 생성 안 함
    bgm: null,
  };

  /**
   * 정답 효과음 재생 (상승하는 팡파레)
   */
  playCorrect() {
    this.play('correct');
  }

  /**
   * 오답 효과음 재생 (하강음)
   */
  playWrong() {
    this.play('wrong');
  }

  /**
   * 클릭 효과음 재생 (틱)
   */
  playClick() {
    this.play('click');
  }

  /**
   * 콤보 효과음 재생 (코인 소리)
   */
  playCombo() {
    this.play('combo');
  }

  /**
   * 레벨업 효과음 재생 (축하 사운드)
   */
  playLevelUp() {
    this.play('levelup');
  }

  /**
   * 배경음악 재생 (향후 구현)
   */
  playBGM() {
    console.log('🎵 BGM 재생 (향후 구현)');
  }

  /**
   * 배경음악 정지
   */
  stopBGM() {
    console.log('🎵 BGM 정지');
  }

  /**
   * 사운드 재생 (내부 메서드)
   * ZzFX로 8bit 사운드 실시간 생성
   */
  private play(type: SoundType) {
    if (this.isMuted) return;

    const params = this.soundParams[type];
    if (params) {
      try {
        // ZzFX로 사운드 생성 및 재생
        zzfx(...params.map((p: number, i: number) => i === 0 ? p * this.volume : p));
        console.log(`🔊 ${type} 효과음 재생 (ZzFX)`);
      } catch (error) {
        console.warn(`사운드 재생 실패: ${type}`, error);
      }
    }
  }

  /**
   * 볼륨 설정 (0.0 ~ 1.0)
   */
  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * 현재 볼륨 반환
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * 음소거 토글
   */
  toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  /**
   * 음소거 여부 반환
   */
  getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * 음소거 설정
   */
  setMuted(muted: boolean) {
    this.isMuted = muted;
  }

  /**
   * 모든 사운드 정지
   */
  stopAll() {
    // ZzFX는 자동으로 정지되므로 별도 처리 불필요
  }
}

// 싱글톤 인스턴스 export
export const soundManager = new SoundManager();
